/**
 * PrioritiesLogo — inline SVG mark + wordmark
 *
 * Light mode: slate-blue bars on white background
 * Dark mode:  lighter bars on dark background
 */

import { useTheme } from '../../contexts/ThemeContext';

interface PrioritiesLogoProps {
  /** Show just the mark (no wordmark text) */
  markOnly?: boolean;
  /** Height of the mark in px — wordmark scales proportionally */
  height?: number;
  className?: string;
}

// Light palette (original brand colors)
const LIGHT = {
  c1: '#424c68',
  c2: '#bdbec5',
  c3: '#4f597a',
  c4: '#9399ad',
  c5: '#aaadb9',
  c6: '#6a718b',
};

// Dark palette — same hues, lifted ~25% brighter
const DARK = {
  c1: '#7b8db8',
  c2: '#d8dae2',
  c3: '#8d9cc0',
  c4: '#b8bccb',
  c5: '#c6c9d4',
  c6: '#9ba4be',
};

export function PrioritiesLogo({ markOnly = false, height = 28, className = '' }: PrioritiesLogoProps) {
  const { isDark } = useTheme();
  const c = isDark ? DARK : LIGHT;

  // Original viewBox: 142.85 × 94.47 → aspect ratio ~1.512
  const markWidth = Math.round(height * (142.85 / 94.47));

  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      {/* Mark */}
      <svg
        width={markWidth}
        height={height}
        viewBox="0 0 142.85 94.47"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* bottom bar — widest */}
        <rect fill={c.c2} x="0"     y="82.27" width="142.85" height="12.2" />
        {/* second bar */}
        <rect fill={c.c5} x="15.02" y="65.76" width="112.82" height="12.2" />
        {/* third bar */}
        <rect fill={c.c4} x="28.15" y="49.25" width="86.54"  height="12.2" />
        {/* fourth bar */}
        <rect fill={c.c6} x="39.42" y="32.73" width="64.02"  height="12.2" />
        {/* fifth bar */}
        <rect fill={c.c3} x="48.8"  y="16.22" width="45.25"  height="12.2" />
        {/* top bar — narrowest */}
        <rect fill={c.c1} x="55.4"  y="0"     width="32.34"  height="11.61" />
      </svg>

      {/* Wordmark */}
      {!markOnly && (
        <span
          style={{
            fontFamily: "'fieldwork', 'degular-variable', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
            fontSize: `${Math.round(height * 0.6)}px`,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            lineHeight: 1,
            color: isDark ? 'var(--text-pri)' : '#1e2333',
            userSelect: 'none',
          }}
        >
          Priorities
          <span style={{ color: isDark ? 'var(--text-ter)' : c.c6, fontWeight: 400 }}>.ai</span>
        </span>
      )}
    </span>
  );
}
